import { Reveal, FlightPathDivider } from "@/components/Dividers";
import { ArrowRight } from "lucide-react";

const Barcode = () => {
  const bars = [3, 1, 2, 1, 1, 4, 1, 2, 3, 1, 1, 2, 4, 1, 3, 2, 1, 1, 2, 3, 1, 4, 2, 1, 1, 3, 2, 1];
  let x = 0;
  return (
    <svg viewBox="0 0 120 36" className="h-9 w-28" aria-hidden="true">
      {bars.map((w, i) => {
        const rect = i % 2 === 0 ? <rect key={`bar-${x}`} x={x} y={0} width={w * 2.2} height={36} fill="rgba(255,255,255,0.85)" /> : null;
        x += w * 2.2;
        return rect;
      })}
    </svg>
  );
};

const passes = [
  {
    flight: "MDPP",
    role: "Forge Medical Devices",
    from: "LAB",
    to: "IMPACT",
    date: "ON BOARD",
    seat: "1A",
    gate: "NOW",
    status: "BOARDING",
    accent: true,
    body: "Currently assisting at the Medical Devices Partnership Program — turning clinical problems into engineered solutions, one prototype at a time.",
  },
  {
    flight: "STUDY SMART",
    role: "Study Smart",
    from: "IDEA",
    to: "LAUNCH",
    date: "FOUNDED",
    seat: "2F",
    gate: "EDUTECH",
    status: "DEPARTED",
    accent: false,
    body: "Co-founded and built an edtech platform from zero — product, students, feedback loops, and the beautiful chaos of a founding team.",
  },
  {
    flight: "STONE & CHALK",
    role: "Events Assistant",
    from: "INNOVATION",
    to: "STAGE",
    date: "IN FLIGHT",
    seat: "3C",
    gate: "S&C",
    status: "IN TRANSIT",
    accent: false,
    body: "Working inside Stone & Chalk's innovation hub — surrounded by founders, mentors, and the day-to-day machinery of getting medtech ideas off the ground.",
  },
  {
    flight: "CONFERENCES",
    role: "Conference Attendee",
    from: "ATTENDEE",
    to: "STAGE",
    date: "MULTI-CITY",
    seat: "4D",
    gate: "TALKS",
    status: "FREQUENT",
    accent: false,
    body: "Collecting stamps at Australia's founder and industry conferences — SouthStart, Blackbird Sunrise, OZWATER and more — where I trade notes with the people building what's next.",
  },
];

const Pass = ({ p, i }) => (
  <Reveal delay={i * 0.15}>
    <div
      className="tile-glow relative flex rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md overflow-hidden"
      data-testid={`boarding-pass-${p.flight.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {/* Stub */}
      <div className={`relative w-24 md:w-28 shrink-0 flex flex-col items-center justify-between py-8 border-r border-dashed border-white/20 ${p.accent ? "bg-[#FF1493]/10" : ""}`}>
        <span className="font-tech text-[10px] tracking-[0.3em] uppercase text-white/40 [writing-mode:vertical-rl] rotate-180">
          Boarding Pass
        </span>
        <span className={`font-display font-black text-3xl ${p.accent ? "text-[#FF1493]" : "text-white/25"}`}>
          {p.flight[0]}
        </span>
        <span className="font-tech text-[10px] text-white/40">{p.seat}</span>
        <span className="absolute -right-3 -top-3 w-6 h-6 rounded-full bg-[#050505]" />
        <span className="absolute -right-3 -bottom-3 w-6 h-6 rounded-full bg-[#050505]" />
      </div>
      {/* Main */}
      <div className="flex-1 p-7 md:p-9">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className={`font-tech text-[10px] tracking-[0.35em] uppercase px-3 py-1 rounded-full border ${p.accent ? "border-[#FF1493] text-[#FF66B2]" : "border-white/20 text-white/50"}`}>
            {p.status}
          </span>
          <span className="font-tech text-[10px] tracking-[0.3em] uppercase text-white/40">FLIGHT · {p.flight}</span>
        </div>
        <div className="flex items-center gap-4 md:gap-6 mt-7">
          <div>
            <p className="font-tech text-[10px] tracking-[0.3em] uppercase text-white/40">DEP</p>
            <p className={`font-display font-black mt-1 ${p.from.length > 8 ? "text-xl md:text-2xl" : "text-3xl md:text-4xl"}`}>{p.from}</p>
          </div>
          {p.to && (
            <>
              <FlightPathMini />
              <div>
                <p className="font-tech text-[10px] tracking-[0.3em] uppercase text-white/40">ARR</p>
                <p className="font-display font-black text-3xl md:text-4xl mt-1 italic text-[#FF1493]">{p.to}</p>
              </div>
            </>
          )}
        </div>
        <h3 className="font-display italic text-2xl md:text-3xl mt-7">{p.role}</h3>
        <p className="text-white/60 text-base leading-relaxed mt-3 max-w-lg">{p.body}</p>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-8">
          <div className="flex gap-8 font-tech text-[10px] tracking-[0.25em] uppercase text-white/45">
            <span>STATUS<br /><span className="text-white/80">{p.date}</span></span>
            <span>GATE<br /><span className="text-white/80">{p.gate}</span></span>
            <span>SEAT<br /><span className="text-white/80">{p.seat}</span></span>
          </div>
          <Barcode />
        </div>
      </div>
    </div>
  </Reveal>
);

const FlightPathMini = () => (
  <svg viewBox="0 0 120 24" className="flex-1 min-w-[80px] h-6" fill="none" aria-hidden="true">
    <path d="M2 12 C 40 2, 80 22, 108 12" stroke="rgba(255,20,147,0.6)" strokeWidth="1.5" strokeDasharray="2 7" strokeLinecap="round" />
    <ArrowRight className="hidden" />
    <circle cx="112" cy="12" r="3" fill="#FF1493" />
  </svg>
);

export const Experience = () => {
  return (
    <section id="experience" className="relative pt-0 pb-28 md:pb-40" data-testid="experience-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="font-tech text-[11px] tracking-[0.4em] uppercase text-[#FF66B2]">Departures Board</p>
          <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight mt-6">
            Experience, <span className="italic text-[#FF1493]">stamped &amp; scanned.</span>
          </h2>
        </Reveal>
        <FlightPathDivider />
        <div className="mt-10 space-y-10 md:ml-12">
          {passes.map((p, i) => (
            <Pass key={p.flight} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
